import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, ArrowRight, ChevronRight, CheckCircle, Star, Lock,
  Rocket, Briefcase, Sparkles, DollarSign, Heart, Play,
  Save, Loader2, Trophy, Target, Users, X, Clock, BookOpen,
  Zap, Award, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import VoiceInput from '@/components/VoiceInput';
import { Card } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const hdr = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' });

const PROJECT_ICONS = {
  side_hustle: { icon: Rocket, emoji: '🚀' },
  personal_brand: { icon: Briefcase, emoji: '💼' },
  glow_up: { icon: Sparkles, emoji: '✨' },
  money_moves: { icon: DollarSign, emoji: '💰' },
  community_impact: { icon: Heart, emoji: '🌍' },
  creator_launch: { icon: Play, emoji: '🎬' },
};

// ==================== FIELD RENDERERS (Enhanced) ====================

const TextField = ({ field, value, onChange }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-1.5">
    <label className="text-sm font-bold text-gray-800">{field.label}</label>
    {field.hint && <p className="text-[10px] text-gray-400 italic">{field.hint}</p>}
    {field.size === 'short' ? (
      <Input value={value || ''} onChange={e => onChange(e.target.value)} placeholder={field.placeholder || ''} className="rounded-2xl border-gray-200 text-sm h-12 px-4" />
    ) : (
      <Textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={field.placeholder || ''} rows={3} className="rounded-2xl border-gray-200 text-sm px-4 py-3" />
    )}
  </motion.div>
);

const ReflectionField = ({ field, value, onChange }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="relative space-y-1.5">
    <div className="flex items-center justify-between">
      <label className="text-sm font-bold text-gray-800">{field.label}</label>
      {field.private && <span className="px-2 py-0.5 rounded-full bg-violet-100 text-violet-600 text-[8px] font-bold">PRIVATE</span>}
    </div>
    {field.hint && <p className="text-[10px] text-gray-400 italic">{field.hint}</p>}
    <Textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={field.placeholder || 'Be real — this is yours...'}
      rows={4} className="rounded-2xl border-violet-200 bg-violet-50/30 text-sm px-4 py-3 focus:bg-white focus:border-violet-400" />
  </motion.div>
);

const ChoiceField = ({ field, value, onChange }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-2">
    <label className="text-sm font-bold text-gray-800">{field.label}</label>
    <div className="flex flex-wrap gap-2">
      {(field.options || []).map((opt, i) => (
        <button key={i} onClick={() => onChange(opt)}
          className={`px-4 py-2.5 rounded-2xl text-sm font-medium transition-all ${value === opt
            ? 'bg-coral text-white shadow-lg scale-105' : 'bg-gray-100 text-gray-700 hover:bg-gray-200 active:scale-95'}`}
          style={value === opt ? { backgroundColor: '#FF4D6D' } : {}}>
          {opt}
        </button>
      ))}
    </div>
  </motion.div>
);

const ChecklistField = ({ field, value, onChange }) => {
  const selected = Array.isArray(value) ? value : [];
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-2">
      <label className="text-sm font-bold text-gray-800">{field.label}</label>
      <div className="space-y-2">
        {(field.options || []).map((opt, i) => {
          const checked = selected.includes(opt);
          return (
            <button key={i} onClick={() => onChange(checked ? selected.filter(s => s !== opt) : [...selected, opt])}
              className={`w-full flex items-center gap-3 p-4 rounded-2xl text-left text-sm transition-all active:scale-[0.98] ${checked
                ? 'bg-white ring-2 shadow-md text-gray-900' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'}`}
              style={checked ? { ringColor: '#FF4D6D' } : {}}>
              <div className={`w-6 h-6 rounded-lg flex-shrink-0 flex items-center justify-center transition-all ${checked ? 'text-white' : 'bg-white border-2 border-gray-200'}`}
                style={checked ? { backgroundColor: '#FF4D6D' } : {}}>
                {checked && <CheckCircle className="w-4 h-4" />}
              </div>
              <span className="flex-1">{opt}</span>
            </button>
          );
        })}
      </div>
    </motion.div>
  );
};

const RatingField = ({ field, value, onChange }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-2">
    <label className="text-sm font-bold text-gray-800">{field.label}</label>
    <div className="flex items-center gap-2">
      {field.low && <span className="text-[9px] text-gray-400 w-12 text-right">{field.low}</span>}
      <div className="flex gap-1.5 flex-1 justify-center">
        {Array.from({ length: field.scale || 5 }, (_, i) => i + 1).map(n => (
          <button key={n} onClick={() => onChange(n)}
            className={`w-12 h-12 rounded-2xl text-sm font-bold transition-all active:scale-90 ${value >= n
              ? 'text-white shadow-lg scale-110' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
            style={value >= n ? { backgroundColor: '#FF4D6D' } : {}}>
            {n}
          </button>
        ))}
      </div>
      {field.high && <span className="text-[9px] text-gray-400 w-12">{field.high}</span>}
    </div>
  </motion.div>
);

const SliderField = ({ field, value, onChange }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-2">
    <div className="flex items-center justify-between">
      <label className="text-sm font-bold text-gray-800">{field.label}</label>
      <span className="text-xl font-black" style={{ color: '#FF4D6D' }}>{field.unit}{value || field.min || 0}</span>
    </div>
    <input type="range" min={field.min || 0} max={field.max || 100} value={value || field.min || 0}
      onChange={e => onChange(parseInt(e.target.value))} className="w-full h-3 rounded-full appearance-none cursor-pointer"
      style={{ accentColor: '#FF4D6D' }} />
    <div className="flex justify-between text-[9px] text-gray-400">
      <span>{field.unit}{field.min || 0}</span><span>{field.unit}{field.max || 100}</span>
    </div>
  </motion.div>
);

const ToggleField = ({ field, value, onChange }) => (
  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-2">
    <label className="text-sm font-bold text-gray-800">{field.label}</label>
    <div className="flex gap-3">
      <button onClick={() => onChange(field.option1)}
        className={`flex-1 py-4 rounded-2xl text-sm font-bold transition-all active:scale-95 ${value === field.option1 ? 'text-white shadow-lg' : 'bg-gray-100 text-gray-600'}`}
        style={value === field.option1 ? { backgroundColor: '#2EC4B6' } : {}}>
        {field.option1}
      </button>
      <button onClick={() => onChange(field.option2)}
        className={`flex-1 py-4 rounded-2xl text-sm font-bold transition-all active:scale-95 ${value === field.option2 ? 'bg-gray-700 text-white shadow-lg' : 'bg-gray-100 text-gray-600'}`}>
        {field.option2}
      </button>
    </div>
  </motion.div>
);

const TableField = ({ field, value, onChange }) => {
  const rows = Array.isArray(value) ? value : Array.from({ length: field.min_rows || 2 }, () => ({}));
  const cols = field.columns || [];
  const updateRow = (ri, cn, val) => { const nr = [...rows]; nr[ri] = { ...nr[ri], [cn]: val }; onChange(nr); };
  const addRow = () => { if (rows.length < (field.max_rows || 10)) onChange([...rows, {}]); };
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-2">
      <label className="text-sm font-bold text-gray-800">{field.label}</label>
      <div className="space-y-2">
        {rows.map((row, ri) => (
          <div key={ri} className="p-4 rounded-2xl bg-white border border-gray-100 shadow-sm space-y-2">
            <span className="text-[9px] font-bold text-gray-300">#{ri + 1}</span>
            {cols.map(col => (
              <Input key={col.name} value={row[col.name] || ''} onChange={e => updateRow(ri, col.name, e.target.value)}
                placeholder={col.name} className="rounded-xl text-sm h-10 border-gray-200" />
            ))}
          </div>
        ))}
      </div>
      {rows.length < (field.max_rows || 10) && (
        <button onClick={addRow} className="w-full py-3 rounded-2xl border-2 border-dashed border-gray-200 text-xs font-bold text-gray-400 hover:border-gray-400 hover:text-gray-600 transition-all">
          + Add another
        </button>
      )}
    </motion.div>
  );
};

const FieldRenderer = ({ field, value, onChange, color }) => {
  const props = { field, value, onChange, color };
  switch (field.type) {
    case 'text': return <TextField {...props} />;
    case 'reflection': return <ReflectionField {...props} />;
    case 'choice': return <ChoiceField {...props} />;
    case 'checklist': return <ChecklistField {...props} />;
    case 'rating': return <RatingField {...props} />;
    case 'slider': return <SliderField {...props} />;
    case 'toggle': return <ToggleField {...props} />;
    case 'table': return <TableField {...props} />;
    default: return <TextField {...props} />;
  }
};

// ==================== MAIN COMPONENT ====================

const ImpactProjectPage = ({ user }) => {
  const navigate = useNavigate();
  const [view, setView] = useState('loading');
  const [projects, setProjects] = useState([]);
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [myProject, setMyProject] = useState(null);
  const [projectData, setProjectData] = useState(null);
  const [sectionIdx, setSectionIdx] = useState(0);
  const [subPageIdx, setSubPageIdx] = useState(0);
  const [responses, setResponses] = useState({});
  const [saving, setSaving] = useState(false);
  const [showCelebration, setShowCelebration] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [projRes, myRes] = await Promise.all([
        fetch(`${API}/api/projects/options`, { headers: hdr() }),
        fetch(`${API}/api/projects/my-project`, { headers: hdr() })
      ]);
      if (projRes.ok) setProjects(await projRes.json());
      if (myRes.ok) {
        const d = await myRes.json();
        if (d && d.submission && d.project_details) {
          setMyProject(d);
          setProjectData(d.project_details);
          setResponses(d.submission.responses || {});
          setSectionIdx(d.submission.current_step || 0);
          setView('workbook');
        } else { setView('selector'); }
      } else { setView('selector'); }
    } catch (e) { setView('selector'); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchData(); }, [fetchData]);

  const selectProject = async (pid) => {
    try {
      const res = await fetch(`${API}/api/projects/select/${pid}`, { method: 'POST', headers: hdr() });
      if (res.ok) { toast.success('Let\'s go!'); setSelectedPreview(null); fetchData(); }
    } catch (e) { toast.error('Failed'); }
  };

  const saveProgress = async (newResp, newStep) => {
    setSaving(true);
    try {
      await fetch(`${API}/api/projects/save-progress`, {
        method: 'POST', headers: hdr(),
        body: JSON.stringify({ responses: newResp || responses, current_step: newStep !== undefined ? newStep : sectionIdx })
      });
    } catch (e) { /* silent */ }
    setSaving(false);
  };

  const goNextSection = async () => {
    const sections = projectData?.sections || [];
    const currentFields = sections[sectionIdx]?.fields || [];
    const fieldsPerPage = 3;
    const totalSubPages = Math.ceil(currentFields.length / fieldsPerPage);
    
    if (subPageIdx < totalSubPages - 1) {
      setSubPageIdx(subPageIdx + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    // Section complete — celebrate then advance
    if (sectionIdx < sections.length - 1) {
      setShowCelebration(true);
      await saveProgress(responses, sectionIdx + 1);
      setTimeout(() => {
        setShowCelebration(false);
        setSectionIdx(sectionIdx + 1);
        setSubPageIdx(0);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 1500);
    } else {
      // PROJECT COMPLETE
      await saveProgress(responses, sectionIdx);
      try {
        await fetch(`${API}/api/projects/submit`, { method: 'POST', headers: hdr() });
      } catch (e) { /* ok */ }
      setView('complete');
    }
  };

  const goPrevSection = () => {
    if (subPageIdx > 0) { setSubPageIdx(subPageIdx - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    if (sectionIdx > 0) { setSectionIdx(sectionIdx - 1); setSubPageIdx(0); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  };

  const jumpToSection = (idx) => {
    if (idx <= sectionIdx + 1) { saveProgress(); setSectionIdx(idx); setSubPageIdx(0); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  };

  // Auto-save
  useEffect(() => {
    if (view !== 'workbook') return;
    const interval = setInterval(() => saveProgress(), 30000);
    return () => clearInterval(interval);
  }, [view, responses, sectionIdx]); // eslint-disable-line

  if (view === 'loading') return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FFF6E9' }}>
      <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#FF4D6D' }} />
    </div>
  );

  // ==================== PROJECT SELECTOR (2x3 Grid) ====================
  if (view === 'selector') {
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#0F1922' }}>
        <div className="max-w-lg mx-auto px-4 py-6">
          <div className="flex items-center gap-3 mb-6">
            <button onClick={() => navigate(-1)} className="p-2 rounded-xl bg-white/10 hover:bg-white/15">
              <ArrowLeft className="w-5 h-5 text-white/70" />
            </button>
            <div>
              <h1 className="text-xl font-black text-white">Impact Projects</h1>
              <p className="text-xs text-white/40">Pick your project. Build something real.</p>
            </div>
          </div>

          {/* 2x3 Grid */}
          <div className="grid grid-cols-2 gap-3">
            {projects.map((p, i) => {
              const iconData = PROJECT_ICONS[p.id] || PROJECT_ICONS.side_hustle;
              return (
                <motion.div key={p.id} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.08 }}
                  onClick={() => setSelectedPreview(p)}
                  className="rounded-2xl overflow-hidden cursor-pointer group hover:scale-[1.03] transition-all">
                  <div className="h-1.5" style={{ backgroundColor: p.color }} />
                  <div className="bg-white p-4">
                    <span className="text-3xl block mb-2">{iconData.emoji}</span>
                    <h3 className="text-sm font-bold text-gray-900 leading-tight">{p.title}</h3>
                    <p className="text-[9px] text-gray-400 mt-1">{p.section_count} sections · {p.estimated_hours}h</p>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Project Overview Modal → FULL PAGE */}
        <AnimatePresence>
          {selectedPreview && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 overflow-y-auto" style={{ backgroundColor: '#FFF6E9' }}>
              {/* Hero header */}
              <div className="h-2" style={{ backgroundColor: selectedPreview.color }} />
              <div className="max-w-lg mx-auto px-4 py-5">
                <button onClick={() => setSelectedPreview(null)} className="p-2 rounded-xl bg-gray-100 hover:bg-gray-200 mb-4">
                  <ArrowLeft className="w-5 h-5 text-gray-500" />
                </button>

                <div className="text-center mb-6">
                  <span className="text-5xl block mb-3">{(PROJECT_ICONS[selectedPreview.id] || {}).emoji || '📋'}</span>
                  <h1 className="text-2xl font-black text-gray-900">{selectedPreview.title}</h1>
                  <p className="text-sm text-gray-500 mt-1">{selectedPreview.subtitle}</p>
                </div>

                <p className="text-sm text-gray-600 leading-relaxed mb-5">{selectedPreview.description}</p>

                {/* Meta badges */}
                <div className="flex flex-wrap gap-2 mb-5">
                  <span className="px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-bold">{selectedPreview.section_count} sections</span>
                  <span className="px-3 py-1.5 rounded-full bg-gray-100 text-gray-600 text-[10px] font-bold">{selectedPreview.estimated_hours} hours</span>
                  <span className="px-3 py-1.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: `${selectedPreview.color}15`, color: selectedPreview.color }}>{selectedPreview.who_for}</span>
                </div>

                {/* What You'll Build */}
                {selectedPreview.what_youll_build?.length > 0 && (
                  <Card className="p-4 rounded-2xl mb-4">
                    <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2"><Target className="w-4 h-4" style={{ color: selectedPreview.color }} /> What You'll Build</h3>
                    <div className="space-y-2">
                      {selectedPreview.what_youll_build.map((item, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: selectedPreview.color }} />
                          <p className="text-xs text-gray-700">{item}</p>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Skills You'll Gain */}
                {selectedPreview.skills_gained?.length > 0 && (
                  <Card className="p-4 rounded-2xl mb-4">
                    <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2"><Zap className="w-4 h-4" style={{ color: selectedPreview.color }} /> Skills You'll Gain</h3>
                    <div className="flex flex-wrap gap-2">
                      {selectedPreview.skills_gained.map((skill, i) => (
                        <span key={i} className="px-3 py-1 rounded-full text-[10px] font-bold" style={{ backgroundColor: `${selectedPreview.color}10`, color: selectedPreview.color }}>{skill}</span>
                      ))}
                    </div>
                  </Card>
                )}

                {/* Section Roadmap */}
                <Card className="p-4 rounded-2xl mb-5">
                  <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4" style={{ color: selectedPreview.color }} /> Your Roadmap</h3>
                  <div className="space-y-2">
                    {(selectedPreview.sections_preview || []).map((s, i) => (
                      <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center text-white text-[10px] font-bold flex-shrink-0" style={{ backgroundColor: selectedPreview.color }}>{s.number}</div>
                        <div className="flex-1">
                          <p className="text-xs font-bold text-gray-800">{s.title}</p>
                          {s.description && <p className="text-[9px] text-gray-400 mt-0.5">{s.description.substring(0, 60)}...</p>}
                        </div>
                        <span className="text-[8px] text-gray-400">{s.field_count} prompts</span>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Cast character */}
                {selectedPreview.cast_character && (
                  <Card className="p-4 rounded-2xl mb-5 border" style={{ borderColor: `${selectedPreview.color}30` }}>
                    <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: selectedPreview.color }}>Your Guide For This Project</p>
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center text-white text-lg font-bold" style={{ backgroundColor: selectedPreview.color }}>
                        {selectedPreview.cast_character.charAt(0)}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-gray-900">{selectedPreview.cast_character}</p>
                        <p className="text-[10px] text-gray-500">{(PERSISTENT_CAST.find(c => c.name === selectedPreview.cast_character) || {}).story || ''}</p>
                      </div>
                    </div>
                  </Card>
                )}

                <Button onClick={() => selectProject(selectedPreview.id)} className="w-full py-6 rounded-2xl text-base font-bold text-white shadow-xl active:scale-[0.98]"
                  style={{ backgroundColor: selectedPreview.color }}>
                  Start This Project <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <p className="text-center text-[10px] text-gray-400 mt-3">You can switch projects anytime</p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    );
  }

  // ==================== COMPLETION PAGE ====================
  if (view === 'complete') {
    const manifestoFields = Object.entries(responses).filter(([k]) => k.includes('MANIFESTO'));
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#0F1922' }}>
        <div className="max-w-lg mx-auto px-4 py-8">
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', damping: 10 }} className="text-center mb-8">
            <div className="w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: projectData?.color || '#FF4D6D' }}>
              <Trophy className="w-14 h-14 text-white" />
            </div>
            <h1 className="text-3xl font-black text-white mb-2">Project Complete!</h1>
            <p className="text-white/60">{projectData?.title} — finished and sealed.</p>
            <p className="text-amber-400 font-bold text-lg mt-2">+250 XP earned</p>
          </motion.div>

          {/* Manifesto Card */}
          {manifestoFields.length > 0 && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
              className="p-6 rounded-2xl mb-4" style={{ backgroundColor: projectData?.color || '#FF4D6D' }}>
              <p className="text-white/60 text-[10px] font-bold uppercase tracking-wider mb-3">My Manifesto</p>
              {manifestoFields.map(([key, val]) => (
                <p key={key} className="text-white text-sm mb-2 leading-relaxed">{String(val)}</p>
              ))}
            </motion.div>
          )}

          {/* Action Buttons */}
          <div className="space-y-3 mt-6">
            <Button onClick={async () => {
              try {
                const res = await fetch(`${API}/api/projects/export`, { headers: hdr() });
                if (res.ok) {
                  const d = await res.json();
                  const blob = new Blob([d.text], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = `${(d.title || 'project').replace(/\s/g, '_')}_${(d.user_name || '').replace(/\s/g, '_')}.txt`;
                  a.click(); URL.revokeObjectURL(url);
                  toast.success('Downloaded!');
                }
              } catch (e) { toast.error('Download failed'); }
            }} className="w-full py-5 rounded-2xl bg-white text-gray-900 font-bold text-base">
              <Save className="w-5 h-5 mr-2" /> Download My Project
            </Button>

            <Button onClick={async () => {
              try {
                const res = await fetch(`${API}/api/projects/export`, { headers: hdr() });
                if (res.ok) {
                  const d = await res.json();
                  await navigator.clipboard.writeText(d.text);
                  toast.success('Copied! Paste into Google Docs to save & share');
                }
              } catch (e) { toast.error('Failed'); }
            }} variant="outline" className="w-full py-5 rounded-2xl border-white/30 text-white font-bold text-base hover:bg-white/10">
              <BookOpen className="w-5 h-5 mr-2" /> Copy to Google Docs
            </Button>

            <Button onClick={() => { setView('workbook'); }} variant="outline" className="w-full py-5 rounded-2xl border-white/20 text-white/60 font-bold hover:bg-white/10">
              <ArrowLeft className="w-5 h-5 mr-2" /> Review My Responses
            </Button>

            <Button onClick={() => navigate(-1)} variant="outline" className="w-full py-4 rounded-2xl border-white/10 text-white/40 font-bold hover:bg-white/10">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ==================== CELEBRATION OVERLAY ====================
  if (showCelebration) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0F1922' }}>
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', damping: 10 }} className="text-center px-8">
          <motion.div initial={{ rotate: -180 }} animate={{ rotate: 0 }} transition={{ delay: 0.2 }}>
            <div className="w-24 h-24 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: projectData?.color || '#FF4D6D' }}>
              <Trophy className="w-12 h-12 text-white" />
            </div>
          </motion.div>
          <h2 className="text-2xl font-black text-white mb-2">Section {sectionIdx + 1} Complete!</h2>
          <p className="text-white/60 text-sm">{sectionIdx + 1} of {projectData?.sections?.length || 0} done. Keep going!</p>
          <Progress value={((sectionIdx + 1) / (projectData?.sections?.length || 1)) * 100} className="h-2 mt-4 max-w-xs mx-auto bg-white/10" />
        </motion.div>
      </div>
    );
  }

  // ==================== WORKBOOK PLAYER ====================
  if (!projectData || !projectData.sections) return null;

  const sections = projectData.sections;
  const currentSection = sections[sectionIdx];
  const totalSections = sections.length;
  const allFields = currentSection?.fields || [];
  const fieldsPerPage = 3;
  const totalSubPages = Math.ceil(allFields.length / fieldsPerPage);
  const pageFields = allFields.slice(subPageIdx * fieldsPerPage, (subPageIdx + 1) * fieldsPerPage);
  const isLastSubPage = subPageIdx >= totalSubPages - 1;
  const isLastSection = sectionIdx === totalSections - 1;
  const color = projectData.color || '#FF4D6D';

  return (
    <div className="min-h-screen pb-28" style={{ backgroundColor: '#FFF6E9' }}>
      {/* Header with section map */}
      <div className="sticky top-0 z-40 bg-white border-b border-gray-100">
        <div className="max-w-lg mx-auto px-4 py-2">
          <div className="flex items-center gap-2 mb-2">
            <button onClick={() => { saveProgress(); setView('selector'); }} className="p-1.5 rounded-lg bg-gray-100">
              <ArrowLeft className="w-4 h-4 text-gray-500" />
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-bold uppercase tracking-wider truncate" style={{ color }}>{projectData.title}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className="text-[9px] text-gray-400">{saving ? 'Saving...' : `${sectionIdx + 1}/${totalSections}`}</span>
              <button onClick={() => { saveProgress(); setView('selector'); }} className="text-[9px] px-2 py-1 rounded-lg bg-gray-100 text-gray-500 hover:bg-gray-200 font-bold">
                Switch
              </button>
            </div>
          </div>
          {/* Clickable section pills */}
          <div className="flex gap-1 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {sections.map((s, i) => {
              const done = i < sectionIdx;
              const current = i === sectionIdx;
              const locked = i > sectionIdx + 1;
              return (
                <button key={i} onClick={() => !locked && jumpToSection(i)}
                  className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[9px] font-bold transition-all whitespace-nowrap ${done ? 'bg-emerald-100 text-emerald-700' : current ? 'text-white' : locked ? 'bg-gray-100 text-gray-300' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}
                  style={current ? { backgroundColor: color } : {}}
                  disabled={locked}>
                  {done ? '✓' : i + 1}. {s.title.length > 12 ? s.title.substring(0, 12) + '...' : s.title}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Section Content */}
      <AnimatePresence mode="wait">
        <motion.div key={`${sectionIdx}-${subPageIdx}`} initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }}
          className="max-w-lg mx-auto px-4 py-5">

          {/* Section header (only on first sub-page) */}
          {subPageIdx === 0 && (
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-10 h-10 rounded-2xl flex items-center justify-center text-white text-sm font-black" style={{ backgroundColor: color }}>
                  {currentSection.number}
                </div>
                <span className="text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full" style={{ backgroundColor: `${color}15`, color }}>{currentSection.beat}</span>
              </div>
              <h2 className="text-2xl font-black text-gray-900">{currentSection.title}</h2>
              <p className="text-sm text-gray-500 mt-1 leading-relaxed">{currentSection.description}</p>
            </div>
          )}

          {/* LESSON CONTENT (only on first sub-page — teaches before asking) */}
          {subPageIdx === 0 && currentSection.lesson && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }}
              className="mb-5 p-5 rounded-2xl bg-white border border-gray-100 shadow-sm">
              <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color }}>What You Need To Know</p>
              {currentSection.lesson.split('\n').map((para, pi) => (
                para.trim() && <p key={pi} className={`text-sm text-gray-700 leading-relaxed ${pi > 0 ? 'mt-3' : ''}`}>{para.trim()}</p>
              ))}
            </motion.div>
          )}

          {/* DID YOU KNOW? card */}
          {subPageIdx === 0 && currentSection.did_you_know && (
            <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.25 }}
              className="mb-5 p-4 rounded-2xl bg-sky-50 border border-sky-100">
              <p className="text-[10px] font-bold uppercase tracking-wider text-sky-700 mb-1">Did You Know?</p>
              <p className="text-sm text-sky-800 leading-relaxed">{currentSection.did_you_know}</p>
            </motion.div>
          )}

          {/* PRO TIP card */}
          {subPageIdx === 0 && currentSection.pro_tip && (
            <motion.div initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}
              className="mb-5 p-4 rounded-2xl bg-amber-50 border border-amber-100">
              <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mb-1">Pro Tip</p>
              <p className="text-sm text-amber-800 leading-relaxed">{currentSection.pro_tip}</p>
            </motion.div>
          )}

          {/* Peer voice card (only on first sub-page) */}
          {subPageIdx === 0 && currentSection.peer_voice && (
            <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              className="mb-5 p-4 rounded-2xl bg-white border border-gray-100 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: color }}>
                  {currentSection.peer_voice.name.charAt(0)}
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-800">{currentSection.peer_voice.name}</p>
                  <p className="text-[9px] text-gray-400">Peer Story</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 italic leading-relaxed">"{currentSection.peer_voice.text}"</p>
            </motion.div>
          )}

          {/* Callout */}
          {subPageIdx === 0 && currentSection.callout && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }}
              className={`mb-5 p-4 rounded-2xl border ${currentSection.callout.type === 'ice' ? 'bg-sky-50 border-sky-100' : currentSection.callout.type === 'lemon' ? 'bg-amber-50 border-amber-100' : 'bg-gray-50 border-gray-100'}`}>
              <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color }}>
                {currentSection.callout.type === 'ice' ? 'Plot Twist' : currentSection.callout.type === 'lemon' ? 'Real Talk' : 'Note'}
              </p>
              <p className="text-sm text-gray-700 leading-relaxed">{currentSection.callout.text}</p>
            </motion.div>
          )}

          {/* Sub-page indicator */}
          {totalSubPages > 1 && (
            <div className="flex items-center justify-center gap-1.5 mb-4">
              {Array.from({ length: totalSubPages }, (_, i) => (
                <div key={i} className={`h-1.5 rounded-full transition-all ${i === subPageIdx ? 'w-6' : 'w-1.5'}`}
                  style={{ backgroundColor: i === subPageIdx ? color : '#D1D5DB' }} />
              ))}
            </div>
          )}

          {/* Fields (max 3 per sub-page) */}
          <div className="space-y-6">
            {pageFields.map((field, fi) => {
              const key = `s${sectionIdx}_${field.label?.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 30) || fi}`;
              return (
                <FieldRenderer key={`${sectionIdx}-${subPageIdx}-${fi}`} field={field} value={responses[key]}
                  onChange={v => setResponses(prev => ({ ...prev, [key]: v }))} color={color} />
              );
            })}
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Bottom nav */}
      <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur border-t border-gray-100 px-4 py-3 z-40">
        <div className="max-w-lg mx-auto">
          <div className="flex gap-3">
            <Button variant="outline" onClick={goPrevSection} disabled={sectionIdx === 0 && subPageIdx === 0}
              className="rounded-2xl h-12 px-4 disabled:opacity-20">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <Button onClick={goNextSection} className="flex-1 rounded-2xl h-12 font-bold text-white shadow-lg active:scale-[0.98]"
              style={{ backgroundColor: color }}>
              {isLastSubPage && isLastSection ? 'Complete Project' : isLastSubPage ? 'Next Section' : 'Continue'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
          {/* Export buttons */}
          {sectionIdx >= Math.floor(totalSections / 2) && (
            <div className="flex gap-2 mt-2">
              <button onClick={async () => {
                try {
                  await saveProgress();
                  const res = await fetch(`${API}/api/projects/export`, { headers: hdr() });
                  if (res.ok) {
                    const d = await res.json();
                    const blob = new Blob([d.text], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = `${d.title.replace(/\s/g, '_')}_${d.user_name.replace(/\s/g, '_')}.txt`;
                    a.click(); URL.revokeObjectURL(url);
                    toast.success('Project downloaded!');
                  }
                } catch (e) { toast.error('Download failed'); }
              }} className="flex-1 py-2 rounded-xl text-xs font-bold bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center justify-center gap-1">
                <Save className="w-3 h-3" /> Download
              </button>
              <button onClick={async () => {
                try {
                  await saveProgress();
                  const res = await fetch(`${API}/api/projects/export`, { headers: hdr() });
                  if (res.ok) {
                    const d = await res.json();
                    await navigator.clipboard.writeText(d.text);
                    toast.success('Copied! Paste into Google Docs to share');
                  }
                } catch (e) { toast.error('Copy failed'); }
              }} className="flex-1 py-2 rounded-xl text-xs font-bold bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center justify-center gap-1">
                <BookOpen className="w-3 h-3" /> Copy to Clipboard
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImpactProjectPage;
